import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Modal } from '../components/UI';
import { calculateSettlement } from '../utils/calculator';
import { formatCurrency, formatDate, getFamilyColor, today } from '../utils/helpers';

export default function Settlement() {
  const { expenses, payments, families, addPayment, deletePayment, showToast } = useApp();
  const [payModal, setPayModal] = useState(false);

  if (!Object.keys(families).length) return <div className="page-loading">Loading...</div>;

  const settlement = calculateSettlement(expenses, payments, families);
  const payerName   = settlement.payerFamily   ? families[settlement.payerFamily]?.name   : '';
  const receiverName = settlement.receiverFamily ? families[settlement.receiverFamily]?.name : '';

  const handleDeletePayment = (id) => {
    if (window.confirm('Delete this payment record?')) {
      deletePayment(id);
      showToast('Payment deleted', 'info');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const amount = parseFloat(fd.get('amount'));
    const date   = fd.get('date');
    const note   = fd.get('note') || '';
    if (amount <= 0) { showToast('Amount must be positive', 'error'); return; }
    await addPayment({ amount, date, note });
    showToast('Payment recorded', 'success');
    setPayModal(false);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settlement</h1>
          <p className="page-subtitle">Final settlement between families</p>
        </div>
      </div>

      {/* Hero Card */}
      <div className={`settlement-hero glass-card${settlement.isSettled ? ' settled' : ''}`}>
        {settlement.amount === 0 ? (
          <div className="settlement-balanced">
            <div className="settlement-icon">⚖️</div>
            <h2>All Balanced!</h2>
            <p>No money is owed between families</p>
          </div>
        ) : (
          <>
            <div className="settlement-direction">
              {[
                { fid: settlement.payerFamily, name: payerName },
                null, // arrow
                { fid: settlement.receiverFamily, name: receiverName },
              ].map((item, i) => {
                if (item === null) return (
                  <div key="arrow" className="settlement-arrow">
                    <svg width="40" height="24" viewBox="0 0 40 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="2" y1="12" x2="36" y2="12"/>
                      <polyline points="30 6 36 12 30 18"/>
                    </svg>
                    <span className="settlement-label">pays</span>
                  </div>
                );
                const colors = getFamilyColor(item.fid);
                return (
                  <div key={item.fid} className="settlement-family">
                    <div className="settlement-avatar" style={{ background: colors.bg, color: colors.text, border: `2px solid ${colors.border}` }}>
                      {item.name?.[0] || '?'}
                    </div>
                    <span>{item.name}</span>
                  </div>
                );
              })}
            </div>
            <div className="settlement-amount-big">{formatCurrency(settlement.amount)}</div>
            {settlement.totalSettlementPaid > 0 && (
              <div className="settlement-progress-section">
                <div className="settlement-progress-bar">
                  <div className="settlement-progress-fill" style={{ width: `${Math.min(100, (settlement.totalSettlementPaid / settlement.amount) * 100)}%` }} />
                </div>
                <div className="settlement-progress-info">
                  <span>Paid: {formatCurrency(settlement.totalSettlementPaid)}</span>
                  <span>Remaining: {formatCurrency(settlement.remaining)}</span>
                </div>
              </div>
            )}
            {settlement.isSettled
              ? <div className="settlement-settled-badge">✓ Fully Settled</div>
              : <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => setPayModal(true)}>Record Payment</button>
            }
          </>
        )}
      </div>

      {/* Family Breakdown */}
      <div className="settlement-grid">
        {['family1', 'family2'].map(fid => {
          const fam    = families[fid];
          const data   = settlement.totals[fid];
          const color  = getFamilyColor(fid);
          const diff   = data.difference;
          return (
            <div className="glass-card settlement-family-card" key={fid} style={{ borderTop: `3px solid ${color.border}` }}>
              <h3 style={{ color: color.text }}>{fam.name}</h3>
              <div className="settlement-stat-grid">
                <div className="settlement-stat">
                  <span className="stat-label">Total Paid</span>
                  <span className="stat-value">{formatCurrency(data.paid)}</span>
                </div>
                <div className="settlement-stat">
                  <span className="stat-label">Should Pay</span>
                  <span className="stat-value">{formatCurrency(data.shouldPay)}</span>
                </div>
                <div className="settlement-stat">
                  <span className="stat-label">{diff >= 0 ? 'Overpaid' : 'Underpaid'}</span>
                  <span className={`stat-value ${diff >= 0 ? 'text-green' : 'text-red'}`}>{formatCurrency(Math.abs(diff))}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="glass-card" style={{ marginTop: '1.5rem' }}>
          <h3 className="section-title">Payment History</h3>
          <div className="payment-list">
            {payments.map(p => (
              <div className="payment-row" key={p.id}>
                <div className="payment-info">
                  <span className="payment-date">{formatDate(p.date)}</span>
                  <span className="payment-note">{p.note || 'Settlement payment'}</span>
                </div>
                <div className="payment-amount">{formatCurrency(p.amount)}</div>
                <button className="btn-icon" title="Delete" onClick={() => handleDeletePayment(p.id)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <Modal isOpen={payModal} onClose={() => setPayModal(false)} title="Record Payment">
        <form onSubmit={handleRecordPayment}>
          <div className="form-group">
            <label className="form-label">Amount (₹)</label>
            <input name="amount" type="number" className="form-input" defaultValue={settlement.remaining} required min="0.01" step="0.01" />
          </div>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input name="date" type="date" className="form-input" defaultValue={today()} required />
          </div>
          <div className="form-group">
            <label className="form-label">Note (optional)</label>
            <input name="note" type="text" className="form-input" placeholder="e.g. UPI transfer" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setPayModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Record Payment</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
